import { z } from "zod";
import {
  CHANNELS,
  DELIVERY_STATUSES,
  LIMITS,
  LOCATION_SOURCES,
  PUSH_CHANNELS,
  SUBSCRIPTION_STATUSES,
} from "../constants";
import { DELIVERY_ISSUES } from "../issues";
import { IsoDate, paginated, Uuid } from "./common";
import { CredentialAlias, Delivery, DeliveryEvent, PhoneE164 } from "./messages";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const ExternalId = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[^\s/]+$/, "external_id may not contain spaces or slashes")
  .meta({ description: "The user id in the client application (D-09).", example: "user-123" });

function isTimeZone(tz: string) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const TimeZone = z
  .string()
  .max(64)
  .refine(isTimeZone, "Unknown IANA time zone")
  .meta({ example: "Asia/Jakarta" });

export const LanguageTag = z
  .string()
  .regex(/^[a-zA-Z]{2,3}(-[a-zA-Z0-9]{2,8})*$/, "Use a BCP 47 language tag, e.g. id or en-US")
  .meta({ example: "id" });

// ---------------------------------------------------------------------------
// Requests (public API)
// ---------------------------------------------------------------------------

export const CustomerLocationInput = z
  .strictObject({
    country: z
      .string()
      .regex(/^[A-Za-z]{2}$/, "Use an ISO 3166-1 alpha-2 country code, e.g. ID")
      .transform((v) => v.toUpperCase())
      .optional(),
    region: z.string().max(120).optional().meta({ example: "DKI Jakarta" }),
    city: z.string().max(120).optional().meta({ example: "Jakarta Selatan" }),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    source: z.enum(LOCATION_SOURCES).default("backend").meta({
      description: "`gps` is precise personal data and requires `consent: true` (UU PDP).",
    }),
    consent: z.boolean().optional(),
  })
  .superRefine((loc, ctx) => {
    if ((loc.lat === undefined) !== (loc.lng === undefined)) {
      ctx.addIssue({ code: "custom", path: ["lat"], message: "lat and lng must be sent together" });
    }
    if (loc.source === "gps" && loc.consent !== true) {
      ctx.addIssue({ code: "custom", path: ["consent"], message: "GPS location requires consent: true" });
    }
  })
  .meta({ id: "CustomerLocationInput" });

export const SubscriptionInput = z
  .strictObject({
    channel: z.enum(CHANNELS),
    address: z.string().min(1).max(4096).meta({
      description:
        "Depending on the channel: FCM installation ID (FID) or registration token (D-49), APNs device token, email address, or E.164 phone number.",
    }),
    credential: CredentialAlias.optional().meta({
      description:
        "Push only: alias of the credential that issued the token (D-04). When empty, the message channel item decides.",
    }),
    is_primary: z.boolean().optional().meta({
      description:
        "Email, SMS and WhatsApp: the address used when the customer has several. The first one per channel is primary automatically.",
    }),
    status: z.enum(["active", "unsubscribed"]).optional().meta({
      description: "Send `unsubscribed` when the user opts out of this address. Defaults to `active`.",
    }),
    device: z
      .strictObject({
        platform: z.enum(["android", "ios", "web", "other"]).optional(),
        model: z.string().max(120).optional(),
        os_version: z.string().max(60).optional(),
        app_version: z.string().max(60).optional(),
      })
      .optional(),
  })
  .superRefine((s, ctx) => {
    if (s.channel === "email" && !z.email().safeParse(s.address).success) {
      ctx.addIssue({ code: "custom", path: ["address"], message: "Invalid email address" });
    }
    if ((s.channel === "sms" || s.channel === "whatsapp") && !PhoneE164.safeParse(s.address).success) {
      ctx.addIssue({
        code: "custom",
        path: ["address"],
        message: "Phone number must be in E.164 format, e.g. +6281234567890",
      });
    }
    if (s.credential && !(PUSH_CHANNELS as readonly string[]).includes(s.channel)) {
      ctx.addIssue({
        code: "custom",
        path: ["credential"],
        message: "credential is only used for push channels",
      });
    }
  })
  .meta({ id: "SubscriptionInput" });
export type SubscriptionInput = z.infer<typeof SubscriptionInput>;

export const UpsertCustomerRequest = z
  .strictObject({
    attributes: z
      .record(z.string().max(64), z.unknown())
      .optional()
      .meta({ description: "Merged into the stored attributes. A `null` value removes the key." }),
    tags: z
      .record(z.string().max(64), z.string().max(255).nullable())
      .optional()
      .meta({ description: "Merged into the stored tags. A `null` value removes the key." }),
    language: LanguageTag.nullable().optional(),
    timezone: TimeZone.nullable().optional(),
    location: CustomerLocationInput.nullable().optional().meta({
      description:
        "Replaces the stored location. A lower-priority source never overwrites a higher one younger than 30 days.",
    }),
    last_active_at: z.iso.datetime({ offset: true }).optional(),
    subscriptions: z.array(SubscriptionInput).max(LIMITS.maxSubscriptionsPerCustomer).optional().meta({
      description:
        "Upserted by (channel, address). Existing subscriptions that are not listed are kept. An address owned by another customer of the same app moves to this customer.",
    }),
  })
  .meta({
    id: "UpsertCustomerRequest",
    example: {
      attributes: { name: "Erry", plan: "premium" },
      tags: { segment: "vip" },
      language: "id",
      timezone: "Asia/Jakarta",
      location: { country: "ID", city: "Jakarta Selatan" },
      subscriptions: [
        { channel: "email", address: "erry@aiyo.id" },
        { channel: "whatsapp", address: "+6281234567890" },
        {
          channel: "fcm",
          address: "fcm-device-token",
          credential: "fcm-main",
          device: { platform: "android", app_version: "1.2.0" },
        },
      ],
    },
  });
export type UpsertCustomerRequest = z.infer<typeof UpsertCustomerRequest>;
export type UpsertCustomerInput = z.input<typeof UpsertCustomerRequest>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const Subscription = z
  .object({
    id: Uuid,
    channel: z.enum(CHANNELS),
    address: z.string(),
    status: z.enum(SUBSCRIPTION_STATUSES),
    is_primary: z.boolean(),
    credential_alias: z.string().nullable(),
    device: z.record(z.string(), z.unknown()).nullable(),
    invalid_reason: z.string().nullable().meta({
      description: "Why the gateway stopped using this address, e.g. `fcm_unregistered`.",
    }),
    last_seen_at: IsoDate.nullable(),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "Subscription" });
export type Subscription = z.infer<typeof Subscription>;

export const CustomerLocation = z
  .object({
    country: z.string().nullable(),
    region: z.string().nullable(),
    city: z.string().nullable(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    source: z.enum(LOCATION_SOURCES).nullable(),
    updated_at: IsoDate.nullable(),
  })
  .meta({ id: "CustomerLocation" });

export const Customer = z
  .object({
    id: Uuid,
    app_id: Uuid,
    external_id: z.string(),
    attributes: z.record(z.string(), z.unknown()),
    tags: z.record(z.string(), z.string()),
    language: z.string().nullable(),
    timezone: z.string().nullable(),
    location: CustomerLocation,
    last_active_at: IsoDate.nullable(),
    subscriptions: z.array(Subscription),
    created_at: IsoDate,
    updated_at: IsoDate,
  })
  .meta({ id: "Customer" });
export type Customer = z.infer<typeof Customer>;

export const AppRef = z.object({ id: Uuid, name: z.string() }).meta({ id: "AppRef" });
export type AppRef = z.infer<typeof AppRef>;

export const ChannelSummary = z.object({
  channel: z.enum(CHANNELS),
  active: z.int(),
  total: z.int(),
});

export const CustomerSummary = z
  .object({
    id: Uuid,
    app: AppRef,
    external_id: z.string(),
    name: z.string().nullable().meta({ description: "`attributes.name` when present." }),
    channels: z.array(ChannelSummary),
    country: z.string().nullable(),
    city: z.string().nullable(),
    last_active_at: IsoDate.nullable(),
    created_at: IsoDate,
  })
  .meta({ id: "CustomerSummary" });
export type CustomerSummary = z.infer<typeof CustomerSummary>;

export const CustomerList = paginated(CustomerSummary, "CustomerList");
export type CustomerList = z.infer<typeof CustomerList>;

export const CustomerActivity = z.object({
  days: z.int(),
  total: z.int(),
  delivered: z.int().meta({ description: "Delivered or accepted by the provider." }),
  problems: z.int(),
  last_delivery_at: IsoDate.nullable(),
});

export const AdminCustomer = Customer.extend({
  app: AppRef,
  activity: CustomerActivity,
}).meta({ id: "AdminCustomer" });
export type AdminCustomer = z.infer<typeof AdminCustomer>;

/** Who a broadcast (`to.all_customers`) would reach right now (D-52). */
export const Audience = z
  .object({
    customers: z.int().meta({ description: "All customers of the app." }),
    reachable: z
      .int()
      .meta({ description: "Customers with an active address on at least one of the requested channels." }),
    per_channel: z.array(
      z.object({
        channel: z.enum(CHANNELS),
        customers: z.int().meta({ description: "Customers with an active address on this channel." }),
        addresses: z.int().meta({
          description: "Deliveries a broadcast creates: every device for push, one address otherwise.",
        }),
      }),
    ),
  })
  .meta({ id: "Audience" });
export type Audience = z.infer<typeof Audience>;

export const AudienceQuery = z.object({
  channels: z
    .string()
    .regex(/^[a-z]+(,[a-z]+)*$/)
    .optional()
    .meta({ description: "Comma-separated channels, e.g. `fcm,email`. Defaults to every channel." }),
});

// ---------------------------------------------------------------------------
// Delivery log (D-38)
// ---------------------------------------------------------------------------

export const DELIVERY_OUTCOMES = ["problems", "delivered", "in_progress"] as const;
export type DeliveryOutcome = (typeof DELIVERY_OUTCOMES)[number];

export const DeliveryLogQuery = z.object({
  app_id: Uuid.optional(),
  customer_id: Uuid.optional(),
  message_id: Uuid.optional(),
  channel: z.enum(CHANNELS).optional(),
  status: z.enum(DELIVERY_STATUSES).optional(),
  outcome: z.enum(DELIVERY_OUTCOMES).optional().meta({
    description: "`problems` = the recipient did not get the message (failed, skipped, bounced).",
  }),
  issue: z.enum(DELIVERY_ISSUES).optional(),
  q: z.string().trim().min(1).max(4096).optional().meta({
    description: "Customer external id, email, phone number, push token, delivery id or message id.",
  }),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});
export type DeliveryLogQuery = z.infer<typeof DeliveryLogQuery>;

export const DeliveryLogItem = Delivery.extend({
  app: AppRef,
  preview: z.string().nullable().meta({ description: "Title, subject or text of the message." }),
  test_mode: z.boolean(),
}).meta({ id: "DeliveryLogItem" });
export type DeliveryLogItem = z.infer<typeof DeliveryLogItem>;

export const DeliveryLogList = paginated(DeliveryLogItem, "DeliveryLogList");
export type DeliveryLogList = z.infer<typeof DeliveryLogList>;

/** One delivery with everything support needs: content, events, and the subscription it used. */
export const DeliveryLogDetail = DeliveryLogItem.extend({
  rendered: z.record(z.string(), z.unknown()).nullable(),
  credential_id: Uuid.nullable(),
  subscription: Subscription.nullable(),
  events: z.array(DeliveryEvent),
}).meta({ id: "DeliveryLogDetail" });
export type DeliveryLogDetail = z.infer<typeof DeliveryLogDetail>;
